/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Component imports
import AnalyticTagsNode from '../components/Graphs/Nodes/AnalyticTags/AnalyticTagsNode';
import CallbackNode from '../components/Graphs/Nodes/Callback/CallbackNode';
import DynamicMultiOptionNode from '../components/Graphs/Nodes/DynamicMultiOption/DynamicMultiOptionNode';
import EmailNode from '../components/Graphs/Nodes/Email/EmailNode';
import FileUploadNode from '../components/Graphs/Nodes/FileUpload/FileUploadNode';
import HttpRequestNode from '../components/Graphs/Nodes/HttpRequest/HttpRequestNode';
import IntroNode from '../components/Graphs/Nodes/Intro/IntroNode';
import JumpNode from '../components/Graphs/Nodes/Jump/JumpNode';
import LinkOptionNode from '../components/Graphs/Nodes/Option/LinkOptionNode';
import LiveChatNode from '../components/Graphs/Nodes/LiveChat/LiveChatNode';
import LiveChatRequestNode from '../components/Graphs/Nodes/LiveChat/LiveChatRequestNode';
import MediaNode from '../components/Graphs/Nodes/Media/MediaNode';
import MessageNode from '../components/Graphs/Nodes/Message/MessageNode';
import MultiOptionNode from '../components/Graphs/Nodes/MultiOption/MultiOptionNode';
import OptionNode from '../components/Graphs/Nodes/Option/OptionNode';
import SelectNode from '../components/Graphs/Nodes/Select/SelectNode';
import SwitchCaseNode from '../components/Graphs/Nodes/SwitchCase/SwitchCaseNode';
import SwitchVariableNode from '../components/Graphs/Nodes/SwitchVariable/SwitchVariableNode';
import TextInputNode from '../components/Graphs/Nodes/TextInput/TextInputNode';
import UserFeedbackNode from '../components/Graphs/Nodes/UserFeedback/UserFeedbackNode';
import UserInfoNode from '../components/Graphs/Nodes/UserInfo/UserInfoNode';
import VariableNode from '../components/Graphs/Nodes/Variable/VariableNode';
import ChannelActionNode from '../components/Graphs/Nodes/ChannelAction/ChannelActionNode';
import SwitchQueryNode from '../components/Graphs/Nodes/SwitchQuery/SwitchQueryNode';
import CarouselNode from '../components/Graphs/Nodes/Carousel/CarouselNode';
import UserInputQueryNode from '../components/Graphs/Nodes/UserInput/UserInputQueryNode';
import NlpFlowQueryNode from '../components/Graphs/Nodes/NlpFlowQuery/NlpFlowQueryNode';
import NlpFlowPipelineNode from '../components/Graphs/Nodes/NlpFlowPipeline/NlpFlowPipelineNode';
import ResetSessionNode from '../components/Graphs/Nodes/ResetSession/ResetSessionNode';
import LlmNode from '../components/Graphs/Nodes/LLM/LlmNode';
import CustomResponseNode from '../components/Graphs/Nodes/CustomResponse/CustomResponseNode';
import CsatNode from '../components/Graphs/Nodes/CSAT/CsatNode';
import StickyNoteNode from '../components/Graphs/Nodes/StickyNote/StickyNoteNode';
import SemanticSearchNode from '../components/Graphs/Nodes/SemanticSearch/SemanticSearchNode';
import {
  BranchesOutlined, CheckOutlined, CloudUploadOutlined, ContactsOutlined, FlagOutlined, FormOutlined, GlobalOutlined, MailOutlined,
  MessageOutlined, QuestionCircleOutlined, SmileOutlined, TagsOutlined, ThunderboltOutlined, UnorderedListOutlined, UploadOutlined, VideoCameraOutlined,
  RedoOutlined, StarOutlined, SearchOutlined
} from '@ant-design/icons';
import { DecisionIcon } from '../components/Icons/DecisionIcon';
import { ExternalLinkIcon } from '../components/Icons/ExternalLinkIcon';
import { VariableIcon } from '../components/Icons/VariableIcon';
import { StickyNoteIcon } from '../components/Icons/StickyNoteIcon';
import { SwitchQueryIcon } from '../components/Icons/SwitchQueryIcon';
import { CarouselIcon } from '../components/Icons/CarouselIcon';
import { CodeIcon } from '../components/Icons/CodeIcon';
import { OpenAiIcon } from '../components/Icons/platformIcons/OpenAi';
import { LiveChatIcon } from '../components/Icons/LiveChatIcon';

// Misc imports
import { IS_CAROUSEL_NODE_ENABLED, IS_USER_FEEDBACK_ENABLED, IS_CSAT_NODE_ENABLED } from './env';
import is from '../utils/is';

export const DEFAULT_NODE_WIDTH = 145;
export const DEFAULT_NODE_HEIGHT = 60;

export const NODE_TYPES = {
  // Basic nodes
  INTRO: 'intro',

  // Activity nodes
  MESSAGE: 'message',
  SELECT: 'select',
  OPTION: 'option',
  LINK_OPTION: 'link_option',
  MULTI_OPTION: 'multi_option',
  DYNAMIC_MULTI_OPTION: 'dynamic_multi_option',
  TEXT_INPUT: 'text_input',
  SWITCH_VARIABLE: 'switch_variable',
  SWITCH_CASE: 'switch_case',
  SWITCH_QUERY: 'switch_query',
  SEND_STORED_DATA: 'send_stored_data',
  HTTP_REQUEST: 'http_request',
  JUMP: 'jump',
  USER_FEEDBACK: 'user_feedback_jump',
  LIVECHAT: 'livechat',
  LIVECHAT_REQUEST: 'livechat_request',
  MEDIA: 'media',
  ANALYTIC_TAGS: 'analytic_tags',
  FILE_UPLOAD: 'file_upload',
  USER_INFO: 'user_info',
  VARIABLE: 'variable',
  EMAIL: 'email',
  ACTION: 'action',
  KNOWLEDGE_BASE_ARTICLE: 'knowledge_base_article',
  CAROUSEL: 'carousel',
  RESET: 'reset',
  LLM: 'llm',
  CUSTOM_RESPONSE: 'custom_response',
  INPUT_QUERY: 'input_query',
  CSAT: 'csat',
  STICKY_NOTE: 'sticky_note',
  SEMANTIC_SEARCH: 'semantic_search',

  // NLP Flow nodes
  QUERY: 'query',
  PIPELINE: 'pipeline'
};

export const NODE_SUBCATEGORIES = {
  QUESTIONS: 'questions',
  COLLECT_USER_DATA: 'collect-user-data'
};

export const NODE_CATEGORIES = {
  POPULAR: {
    key: 'popular',
    priority: 1
  },
  SEND_MESSAGE: {
    key: 'send-message',
    priority: 2
  },
  ASK_USER: {
    key: 'ask-user',
    priority: 3
  },
  ACTIONS: {
    key: 'actions',
    priority: 4
  }
};

export const SWITCH_QUERY_HANDLE_IDS = {
  TRUE: 'true',
  FALSE: 'false'
};

export const USER_INPUT_QUERY_HANDLE_IDS = {
  TRUE: 'true',
  FALSE: 'false'
};

export const CAROUSEL_NODE_HANDLE_IDS = {
  NEXT: 'next'
};

export const FILE_UPLOAD_NODE_HANDLE_IDS = {
  UNSUPPORTED_TYPE: 'unsupported-file-type',
  EXCEEDS_SIZE: 'exceeds-size',
  NO_ATTACHMENT: 'no-attachment',
  FAILED: 'failed'
};

export const CSAT_NODE_RATING_STYLES = {
  STARS: 'stars',
  NUMBERS: 'numbers',
  EMOTIONS: 'emotions'
};

const defaultValidTargetNodes = [
  NODE_TYPES.MESSAGE,
  NODE_TYPES.SELECT,
  NODE_TYPES.FILE_UPLOAD,
  NODE_TYPES.MEDIA,
  NODE_TYPES.JUMP,
  NODE_TYPES.HTTP_REQUEST,
  NODE_TYPES.SEND_STORED_DATA,
  NODE_TYPES.SWITCH_VARIABLE,
  NODE_TYPES.LIVECHAT,
  NODE_TYPES.LIVECHAT_REQUEST,
  NODE_TYPES.ANALYTIC_TAGS,
  NODE_TYPES.USER_INFO,
  ...(IS_USER_FEEDBACK_ENABLED ? [NODE_TYPES.USER_FEEDBACK] : []),
  NODE_TYPES.VARIABLE,
  NODE_TYPES.EMAIL,
  NODE_TYPES.ACTION,
  NODE_TYPES.SWITCH_QUERY,
  ...(IS_CAROUSEL_NODE_ENABLED ? [NODE_TYPES.CAROUSEL] : []),
  NODE_TYPES.QUERY,
  NODE_TYPES.PIPELINE,
  NODE_TYPES.RESET,
  NODE_TYPES.LLM,
  NODE_TYPES.CUSTOM_RESPONSE,
  NODE_TYPES.INPUT_QUERY,
  NODE_TYPES.SEMANTIC_SEARCH,
  ...(IS_CSAT_NODE_ENABLED ? [NODE_TYPES.CSAT] : [])
];

export const DEFAULT_NODE_SUBCATEGORY = 'default_category';

// Note: Order here is important, as this is the order the nodes will appear in the <AddNodePanel />
export const NODE_TYPES_PROPERTIES = {
  // Basic nodes
  [NODE_TYPES.SELECT]: {
    component: SelectNode,
    type: NODE_TYPES.SELECT,
    icon: <QuestionCircleOutlined />,
    label: 'Question',
    info: 'To enter a question that the agent will ask the end-user to answer.',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ASK_USER],
    subCategory: NODE_SUBCATEGORIES.QUESTIONS,
    validTargetNodeTypes: [NODE_TYPES.OPTION, NODE_TYPES.LINK_OPTION, NODE_TYPES.MULTI_OPTION, NODE_TYPES.DYNAMIC_MULTI_OPTION, NODE_TYPES.TEXT_INPUT]
  },
  [NODE_TYPES.MESSAGE]: {
    component: MessageNode,
    type: NODE_TYPES.MESSAGE,
    icon: <MessageOutlined />,
    label: 'Message',
    info: 'To send a message to the end-user, like a greeting, or an explanation before a question, or the text that is to be shown after a user has responded with any of the option nodes to a question.',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.SEND_MESSAGE],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },

  // Option nodes
  [NODE_TYPES.OPTION]: {
    component: OptionNode,
    type: NODE_TYPES.OPTION,
    icon: <CheckOutlined />,
    label: 'Option',
    info: 'Connected to a Question node, it provides the end-user with a pre-set answer.',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ASK_USER],
    subCategory: NODE_SUBCATEGORIES.QUESTIONS,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.LINK_OPTION]: {
    component: LinkOptionNode,
    type: NODE_TYPES.LINK_OPTION,
    icon: <ExternalLinkIcon />,
    label: 'Link-Option (Legacy)',
    info: 'Connected to a Question node, it provides the end-user with a pre-set answer that serves as a clickable button navigating the end-user to a specified URL.',
    hidden: true,
    categories: [NODE_CATEGORIES.ASK_USER],
    subCategory: NODE_SUBCATEGORIES.QUESTIONS,
    validTargetNodeTypes: []
  },
  [NODE_TYPES.MULTI_OPTION]: {
    component: MultiOptionNode,
    type: NODE_TYPES.MULTI_OPTION,
    icon: <UnorderedListOutlined />,
    label: 'Multi-Options',
    info: 'Connected to a Question node to provide the end-user with a list of different answers to choose from. ',
    hidden: false,
    categories: [NODE_CATEGORIES.ASK_USER],
    subCategory: NODE_SUBCATEGORIES.QUESTIONS,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.DYNAMIC_MULTI_OPTION]: {
    component: DynamicMultiOptionNode,
    type: NODE_TYPES.DYNAMIC_MULTI_OPTION,
    icon: <UnorderedListOutlined />,
    label: 'Dynamic Options',
    info: `Multiple options that are dynamically inserted through a template data key.`,
    hidden: false,
    categories: [NODE_CATEGORIES.ASK_USER],
    subCategory: NODE_SUBCATEGORIES.QUESTIONS,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.INPUT_QUERY]: {
    component: UserInputQueryNode,
    type: NODE_TYPES.INPUT_QUERY,
    icon: <FormOutlined />,
    label: 'Input collection (Beta)',
    info: 'Collect text input from end-users.',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ASK_USER],
    subCategory: NODE_SUBCATEGORIES.COLLECT_USER_DATA,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.TEXT_INPUT]: {
    component: TextInputNode,
    type: NODE_TYPES.TEXT_INPUT,
    icon: <FormOutlined />,
    label: 'User Input',
    info: 'Free-text field to answer the given question.',
    hidden: false,
    categories: [NODE_CATEGORIES.ASK_USER],
    subCategory: NODE_SUBCATEGORIES.COLLECT_USER_DATA,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.FILE_UPLOAD]: {
    component: FileUploadNode,
    type: NODE_TYPES.FILE_UPLOAD,
    icon: <UploadOutlined />,
    label: 'File Upload',
    info: 'To allow the user to upload a file as a response to a certain prompt.',
    hidden: false,
    categories: [NODE_CATEGORIES.ASK_USER],
    subCategory: NODE_SUBCATEGORIES.COLLECT_USER_DATA,
    validTargetNodeTypes: defaultValidTargetNodes
  },

  // Advanced nodes
  [NODE_TYPES.MEDIA]: {
    component: MediaNode,
    type: NODE_TYPES.MEDIA,
    icon: <VideoCameraOutlined />,
    label: 'Media',
    info: 'To add video or audio in the conversation flow. The supported files are video/mp4 and audio/mpeg.',
    hidden: false,
    categories: [NODE_CATEGORIES.SEND_MESSAGE],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.CUSTOM_RESPONSE]: {
    component: CustomResponseNode,
    type: NODE_TYPES.CUSTOM_RESPONSE,
    icon: <CodeIcon />,
    label: 'Custom Response',
    info: 'Enables the agent editor to enter custom JSON code to create unique responses that are not supported by other nodes.',
    hidden: false,
    categories: [NODE_CATEGORIES.SEND_MESSAGE],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.SEND_STORED_DATA]: {
    component: CallbackNode,
    type: NODE_TYPES.SEND_STORED_DATA,
    icon: <CloudUploadOutlined />,
    label: 'Callback',
    info: 'To configure the settings that trigger a POST of the collected data (interview) to a given endpoint of the flow.',
    hidden: false,
    categories: [NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.HTTP_REQUEST]: {
    component: HttpRequestNode,
    type: NODE_TYPES.HTTP_REQUEST,
    icon: <GlobalOutlined />,
    label: 'HTTP Request',
    info: 'Enables developers to trigger a HTTP request to a given endpoint of the flow.',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.JUMP]: {
    component: JumpNode,
    type: NODE_TYPES.JUMP,
    icon: <DecisionIcon />,
    label: 'Redirect',
    info: 'To trigger a specific entry point when added at a particular end point of the conversation flow.',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: []
  },
  [NODE_TYPES.USER_FEEDBACK]: {
    component: UserFeedbackNode,
    type: NODE_TYPES.USER_FEEDBACK,
    icon: <SmileOutlined />,
    label: 'User Feedback',
    info: 'To collect user feedback for the current flow.',
    hidden: !IS_USER_FEEDBACK_ENABLED,
    categories: [NODE_CATEGORIES.ASK_USER],
    subCategory: NODE_SUBCATEGORIES.COLLECT_USER_DATA,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.LIVECHAT]: {
    component: LiveChatNode,
    type: NODE_TYPES.LIVECHAT,
    icon: <LiveChatIcon />,
    label: 'LiveChat (Legacy)',
    info: 'Triggers the live-chat request, connecting the end-user with the organization’s agents.',
    hidden: true,
    categories: [NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: []
  },
  [NODE_TYPES.LIVECHAT_REQUEST]: {
    component: LiveChatRequestNode,
    type: NODE_TYPES.LIVECHAT_REQUEST,
    icon: <LiveChatIcon />,
    label: 'LiveChat',
    info: 'Triggers the live-chat request, connecting the end-user with the organization’s agents.',
    hidden: false,
    categories: [NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: []
  },
  [NODE_TYPES.ANALYTIC_TAGS]: {
    component: AnalyticTagsNode,
    type: NODE_TYPES.ANALYTIC_TAGS,
    icon: <TagsOutlined />,
    label: 'Tags',
    info: 'If a tag with name "Outcome" is added, then its values will be shown in the Analytics page under the Surveys section.',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.USER_INFO]: {
    component: UserInfoNode,
    type: NODE_TYPES.USER_INFO,
    icon: <ContactsOutlined />,
    label: 'User info',
    info: 'To collect and store end-user personal information (email, name, phone number), as well as custom data, to assist in customized end-user support.',
    hidden: false,
    categories: [NODE_CATEGORIES.ASK_USER],
    subCategory: NODE_SUBCATEGORIES.COLLECT_USER_DATA,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.VARIABLE]: {
    component: VariableNode,
    type: NODE_TYPES.VARIABLE,
    icon: <VariableIcon />,
    label: 'Variable',
    info: 'To define session or user-bound variables.',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.EMAIL]: {
    component: EmailNode,
    type: NODE_TYPES.EMAIL,
    icon: <MailOutlined />,
    label: 'Send email',
    info: 'Allows the user to send an email to a specific address. The email will contain the content specified in the node.',
    hidden: false,
    categories: [NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.ACTION]: {
    component: ChannelActionNode,
    type: NODE_TYPES.ACTION,
    icon: <ThunderboltOutlined />,
    label: 'Channel Action',
    info: 'This node will send a list of actions to the WebChat or Unity channel in order to be executed at the client side.',
    hidden: false,
    categories: [NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.CAROUSEL]: {
    component: CarouselNode,
    type: NODE_TYPES.CAROUSEL,
    canEditInFlowgraph: true,
    icon: <CarouselIcon />,
    label: 'Carousel',
    info: 'To display a series of images or cards that the user can swipe through horizontally. Each card can contain information such as text, images, and buttons.',
    hidden: !IS_CAROUSEL_NODE_ENABLED,
    categories: [NODE_CATEGORIES.SEND_MESSAGE],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.RESET]: {
    component: ResetSessionNode,
    type: NODE_TYPES.RESET,
    icon: <RedoOutlined />,
    label: 'Start new Session',
    info: 'Triggers a resetting of the chat session.',
    hidden: false,
    categories: [NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: []
  },
  [NODE_TYPES.PIPELINE]: {
    component: NlpFlowPipelineNode,
    type: NODE_TYPES.PIPELINE,
    icon: <ThunderboltOutlined />,
    label: 'NLP System',
    info: 'The first query that returns a non-empty response will be used to continue the flow.',
    hidden: false,
    categories: [NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: []
  },
  [NODE_TYPES.LLM]: {
    component: LlmNode,
    type: NODE_TYPES.LLM,
    icon: <OpenAiIcon />,
    label: 'LLM',
    info: 'The LLM node allows the agent to handle more complex tasks by using natural language processing (NLP) to fetch the necessary information required to provide accurate responses to user inquiries. ',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.CSAT]: {
    component: CsatNode,
    type: NODE_TYPES.CSAT,
    icon: <StarOutlined />,
    label: 'CSAT',
    info: 'To collect user feedback for the current flow.',
    hidden: !IS_CSAT_NODE_ENABLED,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ASK_USER],
    subCategory: NODE_SUBCATEGORIES.COLLECT_USER_DATA,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.STICKY_NOTE]: {
    component: StickyNoteNode,
    type: NODE_TYPES.STICKY_NOTE,
    icon: <StickyNoteIcon />,
    label: 'Sticky note',
    info: 'To add a sticky note anywhere in the flow',
    hidden: false,
    categories: [],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: [],
    standalone: true
  },
  // Flow control nodes
  [NODE_TYPES.SWITCH_QUERY]: {
    component: SwitchQueryNode,
    type: NODE_TYPES.SWITCH_QUERY,
    icon: <SwitchQueryIcon />,
    label: 'Flow Control',
    info: 'To design different paths in the flow based on the value of a variable.',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.SWITCH_VARIABLE]: {
    component: SwitchVariableNode,
    type: NODE_TYPES.SWITCH_VARIABLE,
    icon: <BranchesOutlined />,
    label: 'Flow Control (Legacy)',
    info: 'Provides the ability to design different paths in the flow based on the value of a variable. You can add the Flow control node at any part of the flow and connect it to a Flow option node, which contains the conditions.',
    hidden: true,
    categories: [NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: [NODE_TYPES.SWITCH_CASE]
  },
  [NODE_TYPES.SWITCH_CASE]: {
    component: SwitchCaseNode,
    type: NODE_TYPES.SWITCH_CASE,
    icon: <BranchesOutlined />,
    label: 'Flow Option (Legacy)',
    info: 'Connect it to a Flow control node to add the conditions that will define the flow sequence in the Flow control node settings.',
    hidden: true,
    categories: [NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.QUERY]: {
    component: NlpFlowQueryNode,
    type: NODE_TYPES.QUERY,
    icon: <SwitchQueryIcon />,
    label: 'Flow Control',
    info: 'To design different paths in the flow based on the NLP response.',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: [NODE_TYPES.QUERY, NODE_TYPES.PIPELINE]
  },

  // Dev-only nodes
  [NODE_TYPES.INTRO]: {
    component: IntroNode,
    type: NODE_TYPES.INTRO,
    icon: <FlagOutlined />,
    label: 'Intro',
    info: 'The starting point of every flow with a unique ID.',
    hidden: !is.dev(),
    categories: [NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  },
  [NODE_TYPES.SEMANTIC_SEARCH]: {
    component: SemanticSearchNode,
    type: NODE_TYPES.SEMANTIC_SEARCH,
    icon: <SearchOutlined />,
    label: 'Semantic Search',
    info: 'To run semantic search',
    hidden: false,
    categories: [NODE_CATEGORIES.POPULAR, NODE_CATEGORIES.ACTIONS],
    subCategory: DEFAULT_NODE_SUBCATEGORY,
    validTargetNodeTypes: defaultValidTargetNodes
  }
};

export const NODE_TYPES_COMPONENTS = Object.fromEntries(Object.entries(NODE_TYPES_PROPERTIES).map(([key, value]) => [key, value.component]) || {});

export const TEXT_INPUT_TYPES = {
  STRING: 'string',
  EMAIL: 'email',
  DATE: 'date',
  NUMBER: 'number',
  PHONE: 'phone',
  CUSTOM_REGEX: 'customRegex'
};

export const SWITCH_CASE_CONDITIONS = {
  EQUAL: 'equal',
  NOT_EQUAL: 'notEq',
  LESS: 'less',
  GREATER: 'greater',
  LESS_OR_EQUAL: 'lessOrEq',
  GREATER_OR_EQUAL: 'greaterEq',
  CONTAINS: 'contains',
  MATCHES_REGEX: 'matches'
};

export const SWITCH_QUERY_CONDITIONS = {
  EQUAL: ':=',
  NOT_EQUAL: ':!',
  LESS: ':<',
  GREATER: ':>',
  LESS_OR_EQUAL: ':<=',
  CONTAINS: ':',
  GREATER_OR_EQUAL: ':>=',
  MATCHES_REGEX: ':/',
  NULL: ':null',
  NOT_NULL: ':!null'
};

export const ANALYTIC_TAGS_RECOMMENDATIONS = {
  USER_FEEDBACK_POSITIVE: 'User_Feedback_Positive',
  USER_FEEDBACK_NEGATIVE: 'User_Feedback_Negative',
  LIVECHAT_USER_FEEDBACK_POSITIVE: 'Livechat_User_Feedback_Positive',
  LIVECHAT_USER_FEEDBACK_NEGATIVE: 'Livechat_User_Feedback_Negative',
  INTERVIEW_SUCCESSFUL: 'Survey_Successful',
  INTERVIEW_FAILED: 'Survey_Failed'
};

export const SENDBOX_STATUSES = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
  DEFAULT: 'default'
};

export const MEDIA_TYPES = {
  MP4: 'video/mp4',
  MPEG: 'audio/mpeg'
};

export const DEFAULT_FALLBACK_ID = 'DEFAULT-FALLBACK';

export const ANALYTICS_VIEW_MODE_ACTIVE_NODES = [
  NODE_TYPES.MESSAGE,
  NODE_TYPES.INTRO,
  NODE_TYPES.SELECT,
  NODE_TYPES.MEDIA,
  NODE_TYPES.LLM,
  NODE_TYPES.FILE_UPLOAD,
  NODE_TYPES.ANALYTIC_TAGS,
  NODE_TYPES.HTTP_REQUEST,
  NODE_TYPES.EMAIL,
  NODE_TYPES.ACTION
];

export const GRAPH_LAYOUTS = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical'
};
export const DEFAULT_GRAPH_LAYOUT = GRAPH_LAYOUTS.VERTICAL;

export const FLOWGRAPH_CLIPBOARD_KEY = 'HELVIA_FLOWGRAPH_ELEMENTS';
